import {
    BarChartRounded as BarChartIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles/makeStyles';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { updateDashboardFunc } from '../../../lib/helper';
import { CustomField, CustomFieldType, MetricUnit } from '../../../types/common';
import { Dashboard } from '../../../types/files';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: '20px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '85vh',
        },
    },
    dialogTitle: {
        padding: '28px 32px',
        borderBottom: '1px solid #e5e7eb',
    },
    title: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#1e293b',
        margin: 0,
    },
    icon: {
        color: '#64748b',
    },
    dialogContent: {
        padding: '32px',
    },
    sectionLabel: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#64748b',
        marginBottom: '10px',
    },
    hint: {
        fontSize: '13px',
        color: '#94a3b8',
        marginTop: '-6px',
        marginBottom: '12px',
        lineHeight: 1.5,
    },
    nameField: {
        minWidth: 'fit-content',
        width: '100%',
        '& .MuiOutlinedInput-root': {
            height: '45px',
            fontFamily: 'Lato',
            fontSize: '1rem',
            fontWeight: 500,
            color: '#1eb6a8',
            borderRadius: 12,
            background: '#fff',
            '& fieldset': {
                borderWidth: '2px !important',
                borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused': {
                background: '#f0fdf9 !important',
            },
            '&.Mui-error fieldset': {
                borderColor: '#dc2626 !important',
            },
        },
        '& .MuiFormHelperText-root.Mui-error': {
            color: '#dc2626',
            fontFamily: 'Inter',
            fontSize: '0.8rem',
            marginLeft: 0,
        },
    },
    descField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            fontFamily: 'Lato',
            fontSize: '14px',
            color: '#1e293b',
            background: '#fff',
            '& fieldset': {
                borderWidth: '2px !important',
                borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused': {
                background: '#f0fdf9 !important',
            },
        },
    },
    unitGroup: {
        gap: 8,
        '& .MuiToggleButtonGroup-grouped': {
            border: '2px solid #e5e7eb',
            borderRadius: '999px !important',
            margin: 0,
            padding: '6px 18px',
            textTransform: 'none',
            fontFamily: 'Inter',
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#64748b',
            background: '#fff',
            '&:hover': {
                borderColor: '#1eb6a8',
                background: '#f0fdf9',
            },
            '&.Mui-selected': {
                borderColor: '#1eb6a8',
                background: '#e0f7f4',
                color: '#1eb6a8',
                '&:hover': {
                    background: '#d3f2ec',
                },
            },
        },
    },
    addAliasInput: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            height: '45px',
            borderRadius: 12,
            fontFamily: 'Lato',
            fontSize: '1rem',
            background: '#fff',
            '& fieldset': {
                borderWidth: '2px !important',
                borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1eb6a8 !important',
            },
        },
    },
    addAliasButton: {
        height: 45,
        padding: '0 24px',
        borderRadius: 12,
        border: '2px solid #1eb6a8',
        color: '#1eb6a8',
        fontFamily: 'Inter',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#1eb6a8',
            color: 'white',
        },
    },
    aliasList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        minHeight: 52,
        padding: 12,
        background: '#f8f9fc',
        borderRadius: 12,
        border: '2px solid #e5e7eb',
        position: 'relative',
    },
    emptyAliases: {
        color: '#94a3b8',
        fontSize: '14px',
        fontStyle: 'italic',
        margin: 'auto',
    },
    aliasChip: {
        padding: '4px 8px',
        height: 32,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 999,
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 500,
        color: '#475569',
        '& .MuiChip-deleteIcon': {
            color: '#94a3b8',
            fontSize: 18,
            '&:hover': {
                color: '#64748b',
            },
        },
    },
    dialogActions: {
        padding: '20px 32px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    deleteFieldButton: {
        height: '40px',
        color: '#dc2626',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '8px',
        padding: '10px 16px',
        '&:hover': {
            backgroundColor: '#fee',
        },
    },
    backButton: {
        height: '40px',
        padding: '10px 24px',
        border: '2px solid #e5e7eb',
        borderRadius: '10px',
        backgroundColor: 'white',
        color: '#64748b',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            borderColor: '#cbd5e1',
            backgroundColor: 'white',
        },
    },
    saveButton: {
        height: '40px',
        padding: '10px 24px',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        '&.Mui-disabled': {
            backgroundColor: '#e5e7eb',
            color: '#94a3b8',
        },
    },
    saveButtonActive: {
        backgroundColor: '#1eb6a8',
        color: 'white',
        '&:hover': {
            backgroundColor: '#1a9d91',
        },
    },
}));

type UnitOption = { value: MetricUnit; label: string };

const UNIT_OPTIONS: UnitOption[] = [
    { value: MetricUnit.Currency, label: '$ Currency' },
    { value: MetricUnit.Percentage, label: '% Percentage' },
    { value: MetricUnit.Number, label: '# Number' },
];

const ManageCustomMetricsModal: FC<{
    open: boolean;
    filterType?: string;
    initialName?: string;
    onClose: () => void;
}> = ({ open, filterType, initialName, onClose }) => {
    const classes = useStyles();
    const { settingsLoaded, customFields, setCustomFields, tagGroups } = useContext(GroupSettingsContext);
    const { dashboards, setDashboards } = useContext(DashboardsContext);

    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [unit, setUnit] = useState<MetricUnit>(MetricUnit.Currency);
    const [aliases, setAliases] = useState<string[]>([]);
    const [newAlias, setNewAlias] = useState<string>('');

    const isDuplicateName = useMemo(() => {
        const trimmed = name.trim().toLowerCase();
        if (!trimmed) return false;
        if (filterType && trimmed === filterType.trim().toLowerCase()) return false;
        return customFields.some(({ name: n }) => n.trim().toLowerCase() === trimmed)
            || tagGroups.some(({ title }) => title.trim().toLowerCase() === trimmed);
    }, [name, filterType, customFields, tagGroups]);

    const isSaveDisabled = useMemo(() => !name.trim() || isDuplicateName, [name, isDuplicateName]);

    const handleAddAlias = useCallback(() => {
        const trimmed = newAlias.trim();
        if (!trimmed) return;
        if (aliases.some(a => a.toLowerCase() === trimmed.toLowerCase())) {
            setNewAlias('');
            return;
        }
        setAliases(prev => [...prev, trimmed]);
        setNewAlias('');
    }, [newAlias, aliases]);

    const handleRemoveAlias = useCallback((alias: string) => {
        setAliases(prev => prev.filter(a => a !== alias));
    }, []);

    const handleAliasKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddAlias();
        }
    }, [handleAddAlias]);

    const handleSave = useCallback(() => {
        const trimmedName = name.trim();
        const nextField: CustomField = {
            name: trimmedName,
            additionalContextQuery: description || '',
            type: CustomFieldType.Metric,
            aliases,
            unit,
        };

        const renaming = !!filterType && filterType.toLowerCase() !== trimmedName.toLowerCase();

        setCustomFields(prev => {
            const existsIdx = prev.findIndex(({ name: n }) =>
                n.toLowerCase() === (filterType ?? trimmedName).toLowerCase());
            if (existsIdx >= 0) {
                const next = [...prev];
                next[existsIdx] = { ...prev[existsIdx], ...nextField };
                return next;
            }
            return [...prev, nextField];
        });

        if (renaming) {
            const updatedDashboards: Dashboard[] = [];
            dashboards.forEach(dashboard => {
                let renamed = false;
                const updatedDashboard: Dashboard = {
                    ...dashboard,
                    customFields: dashboard.customFields?.map(custom => {
                        if (custom.name.toLowerCase() === filterType!.toLowerCase()) {
                            renamed = true;
                            return { ...custom, name: trimmedName };
                        }
                        return custom;
                    }) ?? [],
                };
                if (renamed) updatedDashboards.push(updatedDashboard);
            });
            if (updatedDashboards.length) {
                Promise.all(updatedDashboards.map(dashboard => updateDashboardFunc(dashboard)))
                    .then(() => setDashboards(prev => prev.map(dashboard =>
                        updatedDashboards.find(updated => updated.id === dashboard.id) ?? dashboard)));
            }
        }

        onClose();
    }, [name, description, unit, aliases, filterType, setCustomFields, dashboards, setDashboards, onClose]);

    const handleDelete = useCallback(() => {
        const target = (filterType ?? name).toLowerCase();
        setCustomFields(prev => prev.filter(custom => custom.name.toLowerCase() !== target));
        onClose();
    }, [filterType, name, setCustomFields, onClose]);

    useEffect(() => {
        if (!settingsLoaded) return;
        if (filterType) {
            const existing = customFields.find(custom => custom.name === filterType);
            if (existing) {
                setName(existing.name);
                setDescription(existing.additionalContextQuery || '');
                setUnit((existing.unit as MetricUnit) || MetricUnit.Currency);
                setAliases((existing.aliases?.filter((a): a is string => !!a)) || []);
            }
        } else if (initialName) {
            setName(initialName);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoaded]);

    return (
        <Dialog open={open} onClose={onClose} className={classes.dialog}>
            <DialogTitle className={classes.dialogTitle}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <BarChartIcon className={classes.icon} />
                    <Typography className={classes.title}>{'Metric fields'}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Stack spacing={3.5}>
                    <Stack spacing={1.25}>
                        <Typography className={classes.sectionLabel}>{'Metric name'}</Typography>
                        <TextField className={classes.nameField}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={isDuplicateName}
                            helperText={isDuplicateName ? 'A field or tag group with this name already exists' : ''}
                            fullWidth />
                    </Stack>

                    <Stack spacing={1.25}>
                        <Typography className={classes.sectionLabel}>{'Metric unit'}</Typography>
                        <Typography className={classes.hint}>
                            {'How this metric should be parsed and stored.'}
                        </Typography>
                        <ToggleButtonGroup className={classes.unitGroup}
                            value={unit}
                            exclusive
                            onChange={(_, next) => { if (next) setUnit(next); }}>
                            {UNIT_OPTIONS.map(({ value, label }) => (
                                <ToggleButton key={value} value={value} disableRipple>
                                    {label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Stack>

                    <Stack spacing={1.25}>
                        <Typography className={classes.sectionLabel}>{'Metric description'}</Typography>
                        <Typography className={classes.hint}>
                            {'Enhance accuracy by providing additional context on how these should get assigned.'}
                        </Typography>
                        <TextField className={classes.descField}
                            placeholder="Enter additional context here..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            multiline fullWidth rows={4} />
                    </Stack>

                    <Stack spacing={1.25}>
                        <Typography className={classes.sectionLabel}>{'Aliases'}</Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <TextField className={classes.addAliasInput}
                                placeholder="Add a new alias..."
                                value={newAlias}
                                onChange={(e) => setNewAlias(e.target.value)}
                                onKeyPress={handleAliasKeyPress}
                                fullWidth />
                            <Button variant="outlined"
                                className={classes.addAliasButton}
                                onClick={handleAddAlias}
                                disableRipple>
                                {'Add'}
                            </Button>
                        </Stack>
                        <Box className={classes.aliasList}>
                            {aliases.length === 0 && (
                                <Typography className={classes.emptyAliases}>
                                    {'No aliases yet'}
                                </Typography>
                            )}
                            {aliases.map(alias => (
                                <Chip key={alias}
                                    className={classes.aliasChip}
                                    label={alias}
                                    onDelete={() => handleRemoveAlias(alias)}
                                    deleteIcon={<Typography sx={{ fontSize: 18, lineHeight: 1 }}>{'×'}</Typography>} />
                            ))}
                        </Box>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button className={classes.deleteFieldButton}
                    onClick={handleDelete}
                    startIcon={<DeleteIcon />}
                    disabled={!filterType}
                    disableRipple>
                    {'Delete field'}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
                    <Button variant="outlined"
                        className={classes.backButton}
                        onClick={onClose}
                        disableRipple>
                        {'Back'}
                    </Button>
                    <Button variant="contained"
                        className={`${classes.saveButton} ${!isSaveDisabled ? classes.saveButtonActive : ''}`}
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        disableRipple>
                        {'Save'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default ManageCustomMetricsModal;
